import { addDays, format, getWeek, isValid, type Locale, lastDayOfMonth, startOfMonth, subDays } from 'date-fns'
import { da, enUS, fi, nb, sv } from 'date-fns/locale'
import i18next from 'i18next'

export type DateRangeCode =
  | 'today'
  | 'yesterday'
  | 'tomorrow'
  | 'month'
  | '15d'
  | '30d'
  | 'last7d'
  | 'last30d'
  | 'custom'

export { format }

export function dateFormatYYYYMMDD(date: Date | number): string {
  return format(date, 'yyyy-MM-dd', { locale: dateLocale() })
}

export function dateFormatDayPadded(date: Date | number): string {
  return format(date, 'dd', { locale: dateLocale() })
}

export function dateFormatWithOrdinal(date: Date | number): string {
  return format(date, 'do', { locale: dateLocale() })
}

export function dateFormatDateMonthShort(date: Date | number): string {
  return format(date, 'd/M', { locale: dateLocale() })
}

export function dateFormatMonthLongText(date: Date | number): string {
  return format(date, 'MMMM', { locale: dateLocale() })
}

export function dateFormatMonthShortText(date: Date | number): string {
  return format(date, 'MMM', { locale: dateLocale() })
}

export function dateFormatWeekdayLongText(date: Date | number): string {
  return format(date, 'EEEE', { locale: dateLocale() })
}

export function dateFormatHHMM(date: Date | number): string {
  return isValid(date) ? format(date, 'HH:mm', { locale: dateLocale() }) : ''
}

export function dateFormatWeekNumber(date: Date | number): number {
  return getWeek(date, { locale: dateLocale() })
}

export function dateFormatIsoDateTime(date: Date): string {
  return format(date, "yyyy-MM-dd'T'HH:mm")
}

export function dateLocale(): Locale {
  switch (i18next.resolvedLanguage) {
    case 'sv':
      return sv
    case 'nb':
      return nb
    case 'fi':
      return fi
    case 'dk':
      return da
    default:
      return enUS
  }
}

export function getDatesFromRange(values: { range: DateRangeCode; from: string; to: string }): {
  from: string
  to: string
} {
  const now = Date.now()

  switch (values.range) {
    case 'today':
      return {
        from: dateFormatYYYYMMDD(now),
        to: dateFormatYYYYMMDD(now)
      }
    case 'yesterday':
      return {
        from: dateFormatYYYYMMDD(subDays(now, 1)),
        to: dateFormatYYYYMMDD(subDays(now, 1))
      }
    case 'tomorrow':
      return {
        from: dateFormatYYYYMMDD(addDays(now, 1)),
        to: dateFormatYYYYMMDD(addDays(now, 1))
      }
    case 'month':
      return {
        from: dateFormatYYYYMMDD(subDays(now, 30)),
        to: dateFormatYYYYMMDD(now)
      }
    case '15d':
      return {
        from: dateFormatYYYYMMDD(subDays(now, 15)),
        to: dateFormatYYYYMMDD(addDays(now, 15))
      }
    case '30d':
      return {
        from: dateFormatYYYYMMDD(subDays(now, 30)),
        to: dateFormatYYYYMMDD(addDays(now, 29))
      }
    case 'last7d':
      return { from: dateFormatYYYYMMDD(subDays(now, 7)), to: dateFormatYYYYMMDD(now) }
    case 'last30d':
      return { from: dateFormatYYYYMMDD(subDays(now, 30)), to: dateFormatYYYYMMDD(now) }
    case 'custom':
      return {
        from: values.from,
        to: values.to
      }
  }
}

export function getDateAtMidnight(dateOrUndefined: Date | undefined) {
  const dt = dateOrUndefined ? new Date(dateOrUndefined) : new Date()
  dt.setHours(0, 0, 0, 0)
  return dt
}

export function getFirstAndLastDayOfMonth(date: Date): [Date, Date] {
  return [startOfMonth(date), lastDayOfMonth(date)]
}
